import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { useLivePrices } from '@/hooks/useLivePrices';

interface PastPick {
  token_symbol: string;
  token_name: string;
  screening_date: string;
  current_price: number;
  composite_score: number;
}

export function PastPerformance() {
  const [pastPicks, setPastPicks] = useState<PastPick[]>([]);
  const [loading, setLoading] = useState(true);
  const { prices } = useLivePrices();

  useEffect(() => {
    fetchPastPicks();
  }, []);

  const fetchPastPicks = async () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const dateStr = sevenDaysAgo.toISOString().split('T')[0];

    const { data } = await supabase
      .from('screening_results')
      .select('token_symbol, token_name, screening_date, current_price, composite_score')
      .eq('screening_date', dateStr)
      .eq('passes_all_criteria', true)
      .order('composite_score', { ascending: false })
      .limit(10);

    setPastPicks(data ?? []);
    setLoading(false);
  };

  if (loading || pastPicks.length === 0) return null;

  const formatPrice = (price: number) =>
    price < 1 ? `$${price.toFixed(4)}` : `$${price.toFixed(2)}`;

  return (
    <div className="mb-10">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-primary" />
        How Past Picks Performed (7 Days Ago)
      </h2>
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="space-y-3">
            {pastPicks.map((pick) => {
              const liveData = prices.get(pick.token_symbol.toUpperCase());
              const livePrice = liveData?.price;
              const entryPrice = pick.current_price;
              const pnl = livePrice ? ((livePrice - entryPrice) / entryPrice) * 100 : null;

              return (
                <div
                  key={`${pick.token_symbol}-${pick.screening_date}`}
                  className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{pick.token_symbol}</span>
                    <span className="text-sm text-muted-foreground">{pick.token_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      Entry: {formatPrice(entryPrice)}
                    </span>
                    {livePrice != null && (
                      <span className="text-sm text-muted-foreground">
                        Now: {formatPrice(livePrice)}
                      </span>
                    )}
                    {pnl !== null ? (
                      <Badge
                        variant="outline"
                        className={`text-xs ${pnl >= 0 ? 'text-success border-success/30' : 'text-destructive border-destructive/30'}`}
                      >
                        {pnl >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}%
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Score: {pick.composite_score}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
